import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Menu, ShieldAlert, Clock, Hourglass } from "lucide-react";
import { WelcomePage } from "./components/WelcomePage";
import { PasswordResetPage } from "./components/PasswordResetPage";
import { LegalPage } from "./components/legal/LegalPage";
import { VOP_VERSION } from "./components/legal/vop/vopVersion";
import { TermsConsentScreen } from "./components/consent/TermsConsentScreen";
import { fetchTermsConsent } from "./services/termsConsentApi";
import { resolveLegalRoute } from "./components/legal/legalContent";
import { Sidebar } from "./components/Sidebar";
import { RailUnavailable } from "./components/RailUnavailable";
import { RenderFaultBoundary } from "./components/RenderFaultBoundary";
import { PageRouter } from "./components/PageRouter";
import { AppLink } from "./components/navigation/AppLink";
import { NavigationGuardDialog } from "./components/navigation/NavigationGuardDialog";
import { WIZARD_STEPS, stepIndexByKey, type WizardPathContext } from "./components/onboarding/wizardSteps";
import type { StepChangeOptions } from "./components/onboarding/OnboardingWizard";
import { Button } from "./components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./components/ui/tooltip";
import { cn } from "./components/ui/utils";
import { PricesTopBarSwitcher } from "./components/rail/PricesTopBarSwitcher";
import { CommandPalette, type CommandPaletteProps } from "./components/navigation/CommandPalette";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { useLanguage } from "./i18n/language-provider";
import type { Translations } from "./i18n/en";
import { useDashboardInit } from "./hooks/useDashboardInit";
import { useOpenPropertyWatch } from "./hooks/useOpenPropertyWatch";
import { useCalendarNavigation } from "./hooks/useCalendarNavigation";
import { useSidebarCollapsed } from "./hooks/useSidebarCollapsed";
import { usePhoneMenu } from "./hooks/usePhoneMenu";
import { useCommandPaletteShortcut } from "./hooks/useCommandPaletteShortcut";
import { useSkin } from "./components/skin-provider";
import { brandLabelToString, resolveBrandLabel } from "./theme/brand";
import {
    buildHostedAuthPath,
    fetchCurrentUser,
    requestPasswordReset,
} from "./services/authApi";
import {
    AccountDeactivatedError,
    AccountExpiredError,
    AccountPendingApprovalError,
} from "./services/apiClient";
import type { FinalizeOnboardingResult } from "./services/onboardingApi";
import { goBack, navigate, navigateUp, useLocation, useRoute } from "./navigation/history";
import { appReturnPath, routePath, type AppRoute } from "./navigation/routes";
import type { PageName } from "./navigation/pages";
import { accountSectionFollowsOpenProperty } from "./components/account/account-sections";
import { ReadOnlyAccessProvider, useFrontDeskAccess } from "./components/access/read-only-access";
import { PropertyOpenElsewhereProvider } from "./components/pms/property-open-elsewhere";
import {
    PropertyOpenedElsewhereNotice,
    PropertyOpenedElsewhereNoticeUnnamed,
} from "./components/pms/PropertyOpenedElsewhereNotice";

function goToRoute(route: AppRoute, options?: { replace?: boolean }): void {
    navigate(routePath(route), options);
}

/**
 * What the browser tab — and so the history menu behind a long press on Back
 * — calls a page. With the brand alone, every entry in that menu read the
 * same word twenty times over.
 */
function pageTitle(page: PageName, t: Translations): string {
    switch (page) {
        case "dashboard": return t.sidebar.dashboard;
        case "performance": return t.sidebar.performance;
        case "recentBookings": return t.sidebar.recentBookings;
        case "forecast": return t.sidebar.forecast;
        case "demandForecast": return t.sidebar.demandForecast;
        case "segmentChannel": return t.sidebar.segmentChannel;
        case "marketConditions": return t.sidebar.marketConditions;
        case "studyArchive": return t.sidebar.studyArchive;
        case "settings": return t.sidebar.settings;
        case "strategies": return t.sidebar.strategies;
        case "drafts": return t.sidebar.drafts;
        case "users": return t.sidebar.userManagement;
        case "priceDraftsAdmin": return t.sidebar.priceDraftsAdmin;
        case "wirePaymentsAdmin": return t.sidebar.wirePaymentsAdmin;
        case "connectionsAdmin": return t.sidebar.connectionsAdmin;
        case "activityAdmin": return t.sidebar.activityAdmin;
        case "strategyHelpAdmin": return t.sidebar.strategyHelpAdmin;
        // The reminders' old address opens the one strategy-help list.
        case "supportRemindersAdmin": return t.sidebar.strategyHelpAdmin;
        case "featuresAdmin": return t.sidebar.featuresAdmin;
        case "connections": return t.connections.title;
        case "onboarding": return t.sidebar.setup;
        case "account": return t.account.title;
    }
}

/**
 * The page's top bar below `md`: the menu's opener beside the product's name,
 * or on the Prices page beside the page's tabs, which the page then leaves out.
 * A child of the access provider, since the front desk has the one tab.
 */
function PhoneTopBar({ route, opener, brand, onNavigate }: {
    route: AppRoute | null;
    opener: ReactNode;
    brand: ReactNode;
    onNavigate: (route: AppRoute) => void;
}) {
    const frontDesk = useFrontDeskAccess();
    const pricesRoute = route?.page === "drafts" && !frontDesk ? route : null;
    return (
        // The tabs are 44px against the opener's 40, so the bar keeps its height with less padding.
        <div className={cn(
            "md:hidden flex items-center gap-2 px-3 border-b border-border bg-background",
            pricesRoute ? "py-1.5" : "py-2",
        )}>
            {opener}
            {pricesRoute ? <PricesTopBarSwitcher route={pricesRoute} onNavigate={onNavigate} /> : brand}
        </div>
    );
}

/**
 * The quick jump and its shortcut, drawn in the signed-in shell alone. A palette that cannot be drawn is put away
 * and tried again the next time it is asked for (`asked`).
 */
function CommandPaletteHost({ asked, onToggle, ...palette }: CommandPaletteProps & { asked: number; onToggle: () => void }) {
    useCommandPaletteShortcut(onToggle);
    return (
        <RenderFaultBoundary
            part="commandPalette"
            retryKey={asked}
            fallback={null}
            onFault={() => palette.onOpenChange(false)}
        >
            <CommandPalette {...palette} />
        </RenderFaultBoundary>
    );
}

// ---------------------------------------------------------------------------
// Authenticated dashboard shell
// ---------------------------------------------------------------------------

function Dashboard({
    onSignOut,
    onResetPassword,
}: {
    onSignOut: () => void;
    onResetPassword: (email?: string | null) => Promise<void>;
}) {
    const { t } = useLanguage();
    const { skin } = useSkin();
    const brandLabel = resolveBrandLabel(skin, {
        optivo: t.sidebar.title,
        previoLine1: t.sidebar.titlePrevioLine1,
        previoLine2: t.sidebar.titlePrevioLine2,
        betterHotelLine1: t.sidebar.titleBetterHotelLine1,
        betterHotelLine2: t.sidebar.titleBetterHotelLine2,
    });
    const brandName = brandLabelToString(brandLabel);
    const location = useLocation();
    const route = useRoute();
    // Folds itself below 1536px until the operator chooses, then remembers.
    const [sidebarCollapsed, toggleSidebarCollapsed] = useSidebarCollapsed();
    // Below `md` the rail is a menu over the page, opened from the top bar.
    const phoneMenu = usePhoneMenu();
    const { closeMenu: closePhoneMenu } = phoneMenu;
    // `asked` ticks with each opening, the palette's boundary trying again on it.
    const [palette, setPalette] = useState({ open: false, asked: 0 });
    const setPaletteOpen = useCallback((open: boolean) => {
        setPalette((current) => (current.open === open ? current : { open, asked: open ? current.asked + 1 : current.asked }));
    }, []);
    const openPalette = useCallback(() => setPaletteOpen(true), [setPaletteOpen]);
    const togglePalette = useCallback(() => {
        setPalette((current) => ({ open: !current.open, asked: current.open ? current.asked : current.asked + 1 }));
    }, []);
    const [onboardingHighestStep, setOnboardingHighestStep] = useState(0);
    // Mirrors what the wizard reports about the path it will walk, so the
    // sidebar rail lists the same steps rather than every step that exists.
    const [onboardingSkipContext, setOnboardingSkipContext] = useState<WizardPathContext>({
        compsetEnabled: false,
    });

    const init = useDashboardInit(goToRoute);
    const cal = useCalendarNavigation();

    /**
     * The address names the page; the checks decide whether it may be shown.
     * A refused page is never mounted — the fallback renders straight away —
     * and the address is then rewritten in place to say where the user
     * actually is, so Back does not lead to a page that would refuse again.
     */
    const requested: AppRoute = route ?? { page: "dashboard" };
    const verdict = init.pmsCheckLoading ? "pending" : init.isPageAllowed(requested);
    // A section of the account settings the reader may not open — the
    // payments, for somebody whose open property is a read-only grant — falls
    // back to the page itself, which opens on a section they may: the page is
    // still theirs, and the dashboard would be a bounce away from it. Only
    // while the page is theirs, though: somebody with no section at all (a
    // viewer whom no administrator gave the AI agents) is refused the page
    // too, and sent where any refused page sends them, or the page would be
    // asked for and refused again.
    const accountPageAllowed = init.isPageAllowed({ page: "account" }) === true;
    const fallback = useMemo<AppRoute>(
        () => (requested.page === "account" && accountPageAllowed ? { page: "account" } : init.fallbackRoute),
        [requested.page, accountPageAllowed, init.fallbackRoute],
    );
    const shown: AppRoute | null =
        verdict === true ? requested : verdict === false ? fallback : null;

    useEffect(() => {
        if (route === null) {
            goToRoute({ page: "dashboard" }, { replace: true });
            return;
        }
        if (verdict === false) {
            goToRoute(fallback, { replace: true });
        }
    }, [route, verdict, fallback]);

    const shownPage = shown?.page ?? null;
    /** The page the rail marks: the one shown, or the one asked for while the checks run. */
    const railPage = shown?.page ?? requested.page;

    const loadedAccess = useMemo(
        () => ({ role: init.activeConnectionRole ?? null, viewerKind: init.activeConnectionViewerKind ?? null }),
        [init.activeConnectionRole, init.activeConnectionViewerKind],
    );

    /**
     * Whether the property the shell loaded its pages for is still the one
     * open. It is server-side state every tab, window and device shares, and
     * a page reads it once: a tab left behind after another one opened a
     * second property showed the first one's strategy over the second one's
     * room types, and wrote to the second under the first one's name (30 Sep
     * 2026). While another property is open, the notice takes the page's
     * place — see `useOpenPropertyWatch`.
     */
    const openProperty = useOpenPropertyWatch({
        loadedConnectionId: init.loadedConnectionId,
        connectionVersion: init.connectionVersion,
        page: shownPage,
        // An access changed on the property still open — an owner narrowed
        // this person to the front desk, or gave them full access — is taken
        // as a switch is: the person read again and the pages loaded afresh,
        // so a front desk is not left with the analytics on screen.
        loadedAccess,
        onAccessChanged: init.handleConnectionChanged,
        // The account page's tokens and AI-agent switch are the person's own:
        // a property opened in another tab changes nothing they show.
        showsProperty: shown?.page !== "account"
            || accountSectionFollowsOpenProperty(shown.section, {
                readOnlyReason: init.readOnlyReason,
                features: init.features,
                wholeAccount: init.accountWholeAccount,
            }),
    });

    /**
     * Arriving on the Prices page from another page opens its calendar at
     * today; moving between its own tabs keeps the dates. A stay on the
     * address (Recent Bookings → "view in calendar") frames its own range,
     * which PageRouter sets in the same commit, so it is left alone.
     */
    const previousShownPage = useRef<PageName | null>(null);
    const arrivingWithStay = shown?.page === "drafts" && shown.stay != null;
    const { handleEnterPrices } = cal;
    useEffect(() => {
        // The first page shown already opened at today (the range's initial
        // state); a moment with nothing shown is not a page left.
        if (shownPage === null) return;
        const previous = previousShownPage.current;
        previousShownPage.current = shownPage;
        if (shownPage !== "drafts" || previous === "drafts" || previous === null) return;
        if (arrivingWithStay) return;
        handleEnterPrices();
    }, [shownPage, arrivingWithStay, handleEnterPrices]);
    useEffect(() => {
        document.title = shownPage ? `${pageTitle(shownPage, t)} · ${brandName}` : brandName;
    }, [shownPage, brandName, t]);

    // Every move closes the mobile rail — Back and Forward included, which
    // never pass through the rail's own click handler.
    useEffect(() => {
        closePhoneMenu();
    }, [location.pathname, location.search, closePhoneMenu]);

    const onboardingStepKey = shown?.page === "onboarding" ? shown.step : undefined;
    const onboardingStep = onboardingStepKey ? stepIndexByKey(onboardingStepKey) : 0;

    /**
     * A wizard step is a place of its own, so moving between steps is a move
     * in the history: Back returns to the previous step instead of leaving the
     * app. Resuming and clamping rewrite the entry rather than adding one, and
     * the wizard's own Back button goes back when that is where it came from.
     */
    const handleOnboardingStepChange = useCallback((step: number, options: StepChangeOptions = {}) => {
        setOnboardingHighestStep((prev) => Math.max(prev, step));
        const key = WIZARD_STEPS[step]?.key;
        if (!key) return;
        const path = routePath({ page: "onboarding", step: key });
        if (options.history === "up") {
            navigateUp(path, { fallback: "push" });
        } else {
            navigate(path, { replace: options.history === "replace" });
        }
    }, []);

    // Show a centered spinner while we verify PMS status
    if (init.pmsCheckLoading) {
        return (
            <div className="h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-3">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    <p className="text-sm text-muted-foreground">{t.app.checkingPms}</p>
                </div>
            </div>
        );
    }

    // Show full-screen "pending admin approval" message
    if (init.accountPendingApproval) {
        return (
            <div className="h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
                    <Clock className="h-16 w-16 text-primary" />
                    <h1 className="text-2xl font-semibold">{t.accountPendingApproval.title}</h1>
                    <p className="text-muted-foreground">{t.accountPendingApproval.message}</p>
                    <Button variant="outline" onClick={onSignOut}>{t.accountPendingApproval.signInWithDifferentAccount}</Button>
                </div>
            </div>
        );
    }

    // Show full-screen deactivation message
    if (init.accountDeactivated) {
        return (
            <div className="h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
                    <ShieldAlert className="h-16 w-16 text-destructive" />
                    <h1 className="text-2xl font-semibold">{t.accountDeactivated.title}</h1>
                    <p className="text-muted-foreground">{t.accountDeactivated.message}</p>
                    <Button variant="outline" onClick={onSignOut}>{t.accountDeactivated.signInWithDifferentAccount}</Button>
                </div>
            </div>
        );
    }

    // While another property is open than the one the tab was loaded for, the
    // notice takes the page's place. The notice and what stands in for it when
    // it cannot be drawn are handed the same, so it is said once.
    const propertyOpenedElsewhere =
        openProperty.openElsewhere && init.loadedConnectionId !== undefined
            ? {
                loadedConnectionId: init.loadedConnectionId,
                openConnectionId: openProperty.openConnectionId,
                holdsUnsavedWork: openProperty.holdsPage,
                onReopened: openProperty.check,
            }
            : null;

    // A read-only viewer is held by no setup gate (see `useDashboardInit`), so the rail and the palette lock nothing for them.
    const analysisLocked = !init.isReadOnly && (init.onboardingRequired || init.analysisRequired);

    // `h-dvh`, not `h-screen`: 100vh on iOS Safari is the *large* viewport — the
    // height the page would have with the toolbars retracted. Every page here
    // scrolls in an inner `flex-1 overflow-auto`, so the document itself never
    // scrolls and the toolbars never retract, which left the bottom ~90px of
    // every screen permanently behind them. The usual dvh-jitter objection
    // needs a scrolling document, so it does not apply.
    //
    // `relative overflow-hidden` makes the shell the containing block and the
    // clip of everything absolutely positioned under it. Without it a box with
    // `position: absolute` and no positioned ancestor — any `sr-only` label —
    // has the *page* for its containing block, and neither the column's
    // `overflow-hidden` below nor a page's scroller clips or moves it: one
    // sitting low in a long list stretched the document, the scroll left at the
    // end of the page chained onto it, and the whole shell, rail included, slid
    // up over a blank page (the holidays list's "Recurring" label, Oct 2026).
    // Fixed boxes (dialogs, toasts, the Ask Optivo window) are unaffected.
    return (
        // Everything under the shell asks this whether the open property may
        // only be read — see `components/access/read-only-access.tsx` — and
        // whether it is still the one open (`components/pms/property-open-elsewhere.tsx`).
        // The kind of a viewer's grant goes with it: `front_desk` narrows what
        // a viewer is shown to the prices and the reservations. A shell that
        // names none reads as `management`, which every grant once was.
        <ReadOnlyAccessProvider
            readOnly={init.isReadOnly}
            reason={init.readOnlyReason}
            viewerKind={init.activeConnectionViewerKind ?? null}
        >
            <PropertyOpenElsewhereProvider openElsewhere={openProperty.changed}>
                <div className="relative h-dvh flex overflow-hidden bg-background">
                    {/* The rail is drawn inside a boundary of its own, so a render
                        fault in it costs the rail and not the page beside it.
                        Each thing it draws from the server is contained further
                        in (see `RenderFaultBoundary` for where they all stand);
                        this one is for what is left: the navigation, the footer,
                        a fixture's own shell. It has to stand out here, since a
                        boundary catches nothing its own component throws.

                        `RailUnavailable` takes the rail's place, at the rail's
                        width, with the reload, the way home and the way out.

                        It is tried again when the page or the property changes.
                        Those are what the rail is drawn from that a move
                        changes: it lists the wizard's steps on the setup page
                        alone, and its fixtures read for the open property. So a
                        fault tied to one of them is gone on the next, and the
                        rail is back without a reload. A try that fails is not
                        seen, and is not reported a second time. */}
                    <RenderFaultBoundary
                        part="rail"
                        retryKey={`${railPage}:${init.activeConnectionId ?? "none"}:${init.connectionVersion}`}
                        fallback={
                            <RailUnavailable
                                currentPage={railPage}
                                brandLabel={brandLabel}
                                collapsed={sidebarCollapsed}
                                onToggleCollapse={toggleSidebarCollapsed}
                                mobileOpen={phoneMenu.open}
                                onMobileClose={closePhoneMenu}
                                onNavigate={init.handleNavigate}
                                onSignOut={onSignOut}
                            />
                        }
                    >
                        <Sidebar
                            currentPage={railPage}
                            currentRoute={shown ?? requested}
                            onNavigate={init.handleNavigate}
                            isPageAllowed={init.isPageAllowed}
                            onResetPassword={() => onResetPassword(init.userEmail)}
                            onSignOut={onSignOut}
                            userRole={init.userRole}
                            userEmail={init.userEmail}
                            userFeatures={init.features}
                            accountWholeAccount={init.accountWholeAccount}
                            brandLabel={brandLabel}
                            analysisRequired={analysisLocked}
                            collapsed={sidebarCollapsed}
                            onToggleCollapse={toggleSidebarCollapsed}
                            mobileOpen={phoneMenu.open}
                            onMobileClose={closePhoneMenu}
                            activeConnectionId={init.activeConnectionId}
                            onConnectionChanged={init.handleConnectionChanged}
                            connectionVersion={init.connectionVersion}
                            onboardingStep={onboardingStep}
                            onOnboardingStepChange={handleOnboardingStepChange}
                            onboardingHighestStep={onboardingHighestStep}
                            onboardingCompleted={init.onboardingCompleted}
                            pmsSetupRequired={Boolean(init.pmsSetupRequired || init.pmsHealthError)}
                            compsetEnabled={onboardingSkipContext.compsetEnabled}
                            unmeasuredSections={onboardingSkipContext.unmeasuredSections}
                            onOpenCommandPalette={openPalette}
                        />
                    </RenderFaultBoundary>

                    <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                        <PhoneTopBar
                            route={shown}
                            onNavigate={init.handleNavigate}
                            /* Opens the rail as a menu over the page. It had no
                               name at all: a screen reader said "button" and
                               nothing else, on the one way to the menu. It
                               goes by the rail's own word, beside the one the
                               menu's X goes by, and says that it opens a
                               dialog and whether that is open.

                               Explained in a tooltip as the rail's icons are,
                               though this bar is drawn below `md` only: that
                               is a width and not a device, and a desktop
                               window narrowed or zoomed past it gets this bar
                               with a pointer and a keyboard. Not for a focus
                               the menu hands back as it closes, which is the
                               one a finger would be shown (`usePhoneMenu`).

                               40px below `sm`, where the root is 16px and an
                               icon button's 2.25rem comes to 36. */
                            opener={(
                                <Tooltip>
                                    <TooltipTrigger asChild onFocus={phoneMenu.onOpenerFocus}>
                                        <Button
                                            ref={phoneMenu.openerRef}
                                            variant="ghost"
                                            size="icon"
                                            onClick={phoneMenu.openMenu}
                                            aria-label={t.sidebar.openMenu}
                                            aria-haspopup="dialog"
                                            aria-expanded={phoneMenu.open}
                                            className="size-10 sm:size-9 flex-shrink-0"
                                        >
                                            <Menu className="h-5 w-5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" align="start">{t.sidebar.openMenu}</TooltipContent>
                                </Tooltip>
                            )}
                            brand={(
                                <AppLink
                                    to={routePath({ page: "dashboard" })}
                                    onNavigate={() => init.handleNavigate({ page: "dashboard" })}
                                    title={brandName}
                                    className="min-w-0 flex-1 text-base font-medium truncate hover:opacity-80 transition-opacity"
                                >
                                    {brandName}
                                </AppLink>
                            )}
                        />

                        {/* While another property is open the page gives way to the
                            notice. A page holding unsaved work is only hidden, so
                            the work survives and the discard guard still asks
                            before a reload; every other page is unmounted, and
                            mounts afresh if the tab's property is opened again.
                            Always wrapped, so hiding it never remounts it. */}
                        {shown && (!openProperty.openElsewhere || openProperty.holdsPage) && (
                            <div
                                hidden={openProperty.openElsewhere}
                                className={openProperty.openElsewhere ? "hidden" : "contents"}
                            >
                                <PageRouter
                                    route={shown}
                                    onNavigate={init.handleNavigate}
                                    userRole={init.userRole}
                                    activeConnectionRole={init.activeConnectionRole}
                                    activeConnectionId={init.activeConnectionId}
                                    currentUserId={init.userId ?? undefined}
                                    userFeatures={init.features}
                                    accountWholeAccount={init.accountWholeAccount}
                                    onFeaturesStale={init.refreshFeatures}
                                    fromDate={cal.fromDate}
                                    toDate={cal.toDate}
                                    activeRangePreference={cal.activeRangePreference}
                                    isLoadingCalendarData={cal.isLoadingCalendarData}
                                    setIsLoadingCalendarData={cal.setIsLoadingCalendarData}
                                    onDateChange={cal.handleDateChange}
                                    onSelectRange={cal.handleSelectRange}
                                    onJumpToToday={cal.handleJumpToToday}
                                    onCreditsChange={init.handleCreditsChange}
                                    onAnalysisCompleted={init.handleAnalysisCompleted}
                                    onOnboardingCompleted={(result?: FinalizeOnboardingResult) => {
                                        const continueInEditor = result?.profile.completionMode === "early";
                                        init.handleOnboardingCompleted(
                                            continueInEditor && result
                                                ? { page: "strategies", strategyId: result.strategyId }
                                                : { page: "dashboard" },
                                        );
                                    }}
                                    onPmsConfigured={init.handlePmsConfigured}
                                    onConnectionChanged={init.handleConnectionChanged}
                                    onConnectionCreated={init.handleConnectionCreated}
                                    onConnectionOpened={init.handleConnectionOpened}
                                    connectionVersion={init.connectionVersion}
                                    pmsSetupRequired={init.pmsSetupRequired}
                                    pmsHealthError={init.pmsHealthError}
                                    onRedoWizard={() => {
                                        setOnboardingHighestStep(0);
                                        init.handleRedoWizard();
                                    }}
                                    onboardingStep={onboardingStep}
                                    onOnboardingStepChange={handleOnboardingStepChange}
                                    onOnboardingHighestStepChange={setOnboardingHighestStep}
                                    onOnboardingSkipContextChange={setOnboardingSkipContext}
                                    onboardingCompleted={init.onboardingCompleted}
                                />
                            </div>
                        )}
                        {/* The notice is drawn inside a boundary of its own. It
                            names both properties from a list it reads itself, the
                            list the rail's switcher reads, and it had nothing
                            above it but the root: a name it could not draw took
                            the rail down with it, and the page kept hidden above
                            with its unsaved work.

                            Not the page's boundary (`PageErrorBoundary`), though
                            the notice stands in a page's place. That one's
                            stand-in is the offer to reload and nothing else. It
                            would say the page could not be loaded, where the page
                            is hidden because another property is open. And it
                            would drop the other way on: opening the tab's
                            property again, which needs the property's id and not
                            its name, and is the one way to save work kept under
                            the notice. So the notice itself stands in, without
                            the names, as it reads whenever its list cannot be
                            read (`PropertyOpenedElsewhereNoticeUnnamed`). It
                            reads no list, so it cannot fail the same way.

                            One part, `propertyOpenedElsewhere`, and not the
                            page's (`page.drafts`). The page under the notice did
                            not fail, and the notice is one piece of code whatever
                            page it hides: filed by page, its one fault would be
                            reported again for every page it was met on.

                            Tried again when either property it names changes,
                            the notice then reading its list again for another
                            name. In effect that is yet another property being
                            opened elsewhere, the tab's own never changing under
                            the notice, and the name that could not be drawn may
                            be the one that went. A try that fails is not seen:
                            until its list arrives the notice draws itself without
                            the names, which is what stands in for it. Once the
                            tab's own property is the one open again, the boundary
                            goes with the notice.

                            The page kept hidden above stands outside this
                            boundary, so it outlives the fault with its work and
                            with its discard guard still registered. The reload
                            offered here is the notice's own and goes through
                            `runWhenUnblocked`, so the app asks before the work is
                            thrown away, which the bare reload of `ReloadOffer`
                            would leave to the browser. A reload taken any other
                            way (the browser's own button, the rail's stand-in)
                            still meets the browser's question, `beforeunload`
                            asking the same guard. At the root neither held: the
                            page went with everything else, and its work with it,
                            unasked. */}
                        {propertyOpenedElsewhere && (
                            <div className="flex-1 overflow-auto">
                                <div className="p-4 sm:p-6 max-w-3xl">
                                    <RenderFaultBoundary
                                        part="propertyOpenedElsewhere"
                                        retryKey={`${init.loadedConnectionId}:${openProperty.openConnectionId ?? "none"}`}
                                        fallback={<PropertyOpenedElsewhereNoticeUnnamed {...propertyOpenedElsewhere} />}
                                    >
                                        <PropertyOpenedElsewhereNotice {...propertyOpenedElsewhere} />
                                    </RenderFaultBoundary>
                                </div>
                            </div>
                        )}
                    </div>

                    <CommandPaletteHost
                        open={palette.open}
                        asked={palette.asked}
                        onOpenChange={setPaletteOpen}
                        onToggle={togglePalette}
                        onNavigate={init.handleNavigate}
                        isPageAllowed={init.isPageAllowed}
                        userRole={init.userRole}
                        analysisRequired={analysisLocked}
                        currentRoute={shown ?? requested}
                        activeConnectionId={init.activeConnectionId}
                        connectionVersion={init.connectionVersion}
                    />
                    <Toaster />
                    <NavigationGuardDialog />
                </div>
            </PropertyOpenElsewhereProvider>
        </ReadOnlyAccessProvider>
    );
}

// ---------------------------------------------------------------------------
// Root application
// ---------------------------------------------------------------------------

export default function App() {
    const { t } = useLanguage();
    const { pathname: path, search } = useLocation();
    const [authChecked, setAuthChecked] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accountState, setAccountState] = useState<
        "pending" | "deactivated" | "expired" | null
    >(null);

    // The general terms, when the signed-in person has still to accept the
    // current wording: shown in place of everything, the approval screens
    // included, since the server refuses them every other request.
    const [termsConsent, setTermsConsent] = useState<{
        email: string | null;
        previousCompanyId: string | null;
        pendingApproval: boolean;
    } | null>(null);
    const [sessionCheck, setSessionCheck] = useState(0);

    // On mount, ask first whether the terms are accepted — they come before
    // the screen an account still waiting for approval is shown — then probe
    // /api/me to determine if the user has a valid session.
    useEffect(() => {
        let cancelled = false;
        fetchTermsConsent().then((read) => {
            if (cancelled) return;
            if (read.kind === "read" && !read.consent.accepted) {
                setTermsConsent({
                    email: read.email,
                    previousCompanyId: read.consent.previousCompanyId,
                    pendingApproval: read.pendingApproval,
                });
                setAuthChecked(true);
                return;
            }
            setTermsConsent(null);
            probeCurrentUser();
        });
        return () => {
            cancelled = true;
        };
    }, [sessionCheck]);

    function probeCurrentUser() {
        fetchCurrentUser()
            .then(() => {
                setIsAuthenticated(true);
                setAccountState(null);
                setAuthChecked(true);
            })
            .catch((err) => {
                if (err instanceof AccountPendingApprovalError) {
                    setAccountState("pending");
                } else if (err instanceof AccountDeactivatedError) {
                    setAccountState("deactivated");
                } else if (err instanceof AccountExpiredError) {
                    setAccountState("expired");
                } else {
                    setAccountState(null);
                }
                setIsAuthenticated(false);
                setAuthChecked(true);
            });
    }

    const handleSignOut = () => {
        window.location.href = "/logto/sign-out";
    };

    const handleResetPassword = async (email?: string | null) => {
        if (!email) {
            navigate("/reset-password");
            return;
        }

        try {
            await requestPasswordReset(email);
            toast.success(t.passwordReset.requestSuccessMessage);
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : t.passwordReset.genericError,
            );
        }
    };

    // Public legal documents — terms, contact details, privacy notice. These
    // come before every session-dependent branch on purpose: the payment
    // gateway reads them logged out, and they must render even if /api/me is
    // slow or unreachable.
    const legalDocumentKey = resolveLegalRoute(path);
    if (legalDocumentKey) {
        return <LegalPage documentKey={legalDocumentKey} onNavigate={navigate} onBack={() => goBack("/")} />;
    }

    if (authChecked && termsConsent) {
        return (
            <TermsConsentScreen
                version={VOP_VERSION}
                email={termsConsent.email}
                previousCompanyId={termsConsent.previousCompanyId}
                pendingApproval={termsConsent.pendingApproval}
                onAccepted={() => {
                    setAuthChecked(false);
                    setTermsConsent(null);
                    setSessionCheck((count) => count + 1);
                }}
                onSignOut={handleSignOut}
            />
        );
    }

    // Account pending admin approval — user has a valid Logto session but the
    // backend rejected them. Show a clear message and a way to switch accounts.
    if (authChecked && accountState === "pending") {
        return (
            <div className="h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
                    <Clock className="h-16 w-16 text-primary" />
                    <h1 className="text-2xl font-semibold">{t.accountPendingApproval.title}</h1>
                    <p className="text-muted-foreground">{t.accountPendingApproval.message}</p>
                    <Button variant="outline" onClick={handleSignOut}>
                        {t.accountPendingApproval.signInWithDifferentAccount}
                    </Button>
                </div>
            </div>
        );
    }

    // Account deactivated by an admin — same idea, different copy.
    if (authChecked && accountState === "deactivated") {
        return (
            <div className="h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
                    <ShieldAlert className="h-16 w-16 text-destructive" />
                    <h1 className="text-2xl font-semibold">{t.accountDeactivated.title}</h1>
                    <p className="text-muted-foreground">{t.accountDeactivated.message}</p>
                    <Button variant="outline" onClick={handleSignOut}>
                        {t.accountDeactivated.signInWithDifferentAccount}
                    </Button>
                </div>
            </div>
        );
    }

    // Temporary access (minted by an admin) that has run out. Its own copy,
    // because the next step differs: nobody switched this account off, the
    // window simply closed, and an admin can open it again.
    if (authChecked && accountState === "expired") {
        return (
            <div className="h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
                    <Hourglass className="h-16 w-16 text-muted-foreground" />
                    <h1 className="text-2xl font-semibold">{t.accountExpired.title}</h1>
                    <p className="text-muted-foreground">{t.accountExpired.message}</p>
                    <Button variant="outline" onClick={handleSignOut}>
                        {t.accountExpired.signInWithDifferentAccount}
                    </Button>
                </div>
            </div>
        );
    }

    // Welcome page at root when not authenticated
    if (path === "/" && authChecked && !isAuthenticated) {
        const handleWelcomeNavigate = (target: "login" | "signup" | "resetPassword") => {
            if (target === "resetPassword") {
                navigate("/reset-password");
                return;
            }
            window.location.href = buildHostedAuthPath(target);
        };
        return <WelcomePage onNavigate={handleWelcomeNavigate} />;
    }

    if (path === "/reset-password" && authChecked) {
        const token = new URLSearchParams(search).get("token");
        return (
            <PasswordResetPage
                token={token}
                onBackToSignIn={() => {
                    window.location.href = buildHostedAuthPath("login");
                }}
            />
        );
    }

    // Show spinner while checking auth
    if (!authChecked) {
        return (
            <div className="h-screen flex items-center justify-center bg-background">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
        );
    }

    // Not authenticated — redirect to sign-in, asking to come back to the page
    // the address named: a bookmark or a link a colleague sent should open that
    // page once the sign-in is done, not the dashboard.
    if (!isAuthenticated) {
        window.location.href = buildHostedAuthPath("login", appReturnPath(path, search));
        return null;
    }

    return <Dashboard onSignOut={handleSignOut} onResetPassword={handleResetPassword} />;
}
